/**
 * Classifies an HTTP error as an authentication failure.
 *
 * The status alone is not usable: the backends answer 403 for expired tokens,
 * business errors and unmapped routes alike, so the body decides. Four shapes
 * exist in the wild — backoffice (`error.error_code`), API Gateway
 * (`message`/`Message`), cloud-proxy (`name` + `error.message`) and route
 * misses. Anything unrecognized is left alone rather than assumed to be auth.
 */

export type AuthFailure =
  /** Refresh the session and replay the request once. */
  | 'RENEWABLE'
  /** Credentials are unusable; drop the session. */
  | 'FATAL'
  /** Not an authentication problem. */
  | 'NONE'

/** Routes that own the credentials: a 401 there is not a stale access token. */
const AUTH_ROUTES = [
  '/login',
  '/sso/login',
  '/refresh-token',
  '/forgot-password',
  '/reset-password',
  '/validate-code',
]

const RENEWABLE_ERROR_CODES = ['TOKEN_EXPIRED']

const FATAL_ERROR_CODES = ['INVALID_TOKEN', 'MISSING_AUTHORIZATION']

const STALE_CREDENTIAL_MESSAGES = [
  /explicit deny/i, // API Gateway authorizer rejected the token
  /^unauthorized$/i, // API Gateway, no token reached it
  /token invalid or expired/i, // cloud-proxy
]

export const isAuthRoute = (pathname: string | undefined): boolean => {
  if (!pathname) {
    return false
  }

  return AUTH_ROUTES.some(
    (route) => pathname === route || pathname.endsWith(route),
  )
}

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === 'object' && value !== null
    ? (value as Record<string, unknown>)
    : null

const asText = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : ''

export const classifyAuthFailure = (
  statusCode: number,
  body: unknown,
): AuthFailure => {
  if (statusCode !== 401 && statusCode !== 403) {
    return 'NONE'
  }

  const root = asRecord(body)
  const error = asRecord(root?.error)

  const errorCode = asText(error?.error_code)

  if (errorCode) {
    if (RENEWABLE_ERROR_CODES.includes(errorCode)) {
      return 'RENEWABLE'
    }

    // Everything else in this envelope (e.g. INVALID_REQUEST) is a business
    // error that happens to travel with a 403.
    return FATAL_ERROR_CODES.includes(errorCode) ? 'FATAL' : 'NONE'
  }

  // 123fala capitalizes `Message`; cloud-proxy puts it in `name`/`error.message`.
  const messages = [root?.message, root?.Message, root?.name, error?.message]
    .map(asText)
    .filter(Boolean)

  const isStale = messages.some((message) =>
    STALE_CREDENTIAL_MESSAGES.some((pattern) => pattern.test(message)),
  )

  return isStale ? 'RENEWABLE' : 'NONE'
}
